import { html } from "lit";

import { customElement, state } from "lit/decorators.js";

import { WowFormController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthService } from "ui-common/services/auth/types";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/divider";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/wow-link";
import "ui-components/wow-logo";
import { isValidPassword } from "helpers/password";

import { styles } from "./wow-sign-up-create.css";

import privatePolicy from "../../../assets/Privacy-Policy.pdf";
import termsAndConditions from "../../../assets/Terms-and-Conditions.pdf";
import "../../components/social-sign-up-buttons/apple-sign-up-button";
import "../../components/social-sign-up-buttons/facebook-sign-up-button";
import "../../components/social-sign-up-buttons/google-sign-up-button";
import { t, translate } from "../../translations/i18n";
import { navigateTo } from "../../router";

interface SignUpFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

function timingSafeEqual(a: string, b: string) {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

@customElement("wow-sign-up-create")
export class WowSignUpCreate extends SignalElement {
  @inject<IAuthService>("authService")
  private authService!: IAuthService;

  static override styles = [styles];

  @state()
  private signUpError = "";

  @state()
  private isSubmitting = false;

  private signUpFormController: WowFormController<SignUpFormData> = new WowFormController(
    this,
    {
      email: "",
      password: "",
      confirmPassword: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validateOnInputWhenDirty: false,
      validators: {
        email: [
          (value: string) => {
            if (!value) {
              return translate("sign-up.emailRequired");
            }
            if (!/\S+@\S+\.\S+/.test(value)) {
              return translate("sign-up.invalidEmail");
            }
            return;
          },
        ],
        password: [
          (value: string) => {
            if (!value) return translate("sign-up.passwordRequired");

            const { minLength, maxLength, hasNumber, hasUpperLowerCase } = isValidPassword(value);

            if (!minLength) return translate("sign-up.passwordMinLength");
            if (!maxLength) return translate("sign-up.passwordMaxLength");
            if (!hasNumber) return translate("sign-up.passwordNumber");
            if (!hasUpperLowerCase) return translate("sign-up.passwordCase");

            return;
          },
        ],
        confirmPassword: [
          (value: string) => {
            const password = this.signUpFormController.getValue("password") as string;

            if (!value) {
              return translate("sign-up.passwordRequired");
            }

            if (!timingSafeEqual(password, value)) {
              return translate("sign-up.passwordMismatch");
            }
            return;
          },
        ],
      },
    },
    async (event) => {
      this.signUpError = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;

      if (!isValid) {
        this.signUpError = translate("sign-up.fixErrors");
        return;
      }

      this.isSubmitting = true;
      try {
        const password = this.signUpFormController.getValue("password") as string;
        const confirmPasswordInput = this.renderRoot.querySelector(
          'wow-input[name="confirmPassword"]',
        ) as HTMLInputElement;

        if (!confirmPasswordInput.value) {
          confirmPasswordInput.setCustomValidity(translate("sign-up.passwordRequired"));
          this.signUpFormController.reportValidity();
          return;
        }
        if (!timingSafeEqual(password, confirmPasswordInput.value)) {
          confirmPasswordInput.setCustomValidity(translate("sign-up.passwordMismatch"));
          this.signUpFormController.reportValidity();
          return;
        }

        const result = await rpc("signUp", {
          email: data.email.toLowerCase(),
          password: data.password,
          confirmPassword: data.confirmPassword,
        });

        if (result.accessToken == undefined) {
          return;
        }

        // Auto-signin user with the returned access token
        this.authService.signIn(result.accessToken);

        // Navigate directly to theme selection (step 1 in the 4-step fan signup wizard)
        navigateTo("sign-up", true, ["themes"]);
      } catch (error) {
        if (typeof error === "string" && error.trim().length > 0) {
          this.signUpError = error;
        } else if (error instanceof Error && error.message) {
          this.signUpError = error.message;
        } else {
          this.signUpError = translate("sign-up.generalError");
        }

        console.error("Sign up error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
    "#sign-up-form",
  );

  override render() {
    // <wow-divider>${t("sign-up.or")}</wow-divider>
    return html`
      <form id="sign-up-form">
        <div class="logo">
          <wow-logo></wow-logo>
        </div>

        <div class="main-content">
          <div class="header">
            <div class="title-section">
              <h1 class="title">${t("sign-up.title")}</h1>
              <p class="subtitle">${t("sign-up.subtitle")}</p>
            </div>

            <div class="social-buttons">
              <google-login-button></google-login-button>
              <!-- <apple-login-button></apple-login-button> -->
              <facebook-login-button></facebook-login-button>
            </div>
            <wow-divider>${t("sign-in.or")}</wow-divider>
          </div>

          <div class="form-section">
            <wow-input
              form="sign-up-form"
              name=${this.signUpFormController.fieldNames.email}
              type="email"
              placeholder=${t("sign-up.email")}
              autocomplete="email"
              required
            >
            </wow-input>

            <wow-input
              form="sign-up-form"
              name=${this.signUpFormController.fieldNames.password}
              type="password"
              placeholder=${t("sign-up.password")}
              autocomplete="new-password"
              required
            ></wow-input>

            <wow-input
              form="sign-up-form"
              name=${this.signUpFormController.fieldNames.confirmPassword}
              type="password"
              placeholder=${t("sign-up.confirmPassword")}
              autocomplete="new-password"
              required
            ></wow-input>

            ${this.signUpError ? html`<div class="form-error">${this.signUpError}</div>` : ""}
          </div>
        </div>
      </form>

      <div class="footer">
        <wow-button class="submit-button" form="sign-up-form" type="submit" ?disabled=${this.isSubmitting}>
          ${this.isSubmitting ? t("sign-up.submitting") || "Submitting..." : t("sign-up.continue")}
        </wow-button>
        <div>
          ${t("sign-up.haveAccount")}
          <wow-link href="sign-in"> ${t("sign-up.signIn")} </wow-link>
        </div>
        <p class="terms-text">
          ${t("sign-up.termsAndConditions")}
          <a href=${"/static/" + termsAndConditions} target="_blank" rel="noopener noreferrer" style="color: inherit;"
            >${t("sign-up.termsOfServiceLink")}</a
          >
          ${t("sign-up.and")}
          <a href=${"/static/" + privatePolicy} target="_blank" rel="noopener noreferrer" style="color: inherit;"
            >${t("sign-up.privacyPolicyLink")}</a
          >
          ${t("sign-up.byWorldOfWow")}
        </p>
      </div>
    `;
  }
}
